import {
  DOCS_FORM_NAMES,
  EARLY_REHAB_CRITERIA,
  GENDER,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import { getUnixTime } from "date-fns";
import {
  ControlledInput,
  ControlledNumberInput,
  ControlledRadioGroup,
  ControlledTextarea,
} from "ds/ui";
import Translations from "translations/types";
import { FormFieldConfig } from "..";
import {
  ControlledCheckboxGroupWithFocus,
  ControlledDatePickerWithFocus,
  ControlledRadioGroupWithFocus,
} from "../../utils";
import {
  ComplicationInTreatmentProcessField,
  getSingerRadioOptions,
  ICDCodeFields,
  InfectionsStateField,
  RecommendedRehaTypeField,
  RehabAdmissionTypeField,
  RehabDirectiveStateField,
  RehabFocusField,
  RehaCapacityField,
  SINGER_DOMAIN_SCORE_FIELDS,
  SingerAssessmentSectionSubTitle,
  SingerAssessmentSectionTitle,
  SpecialRequirementsRequiredField,
  SurgeryPerformedField,
  TransportTypeField,
} from "./fields";

const getGenderOptions = (translations: Translations) => {
  return [
    {
      label: translations.patientForms.medicalForm.sectionOne.genderMale,
      value: GENDER.MALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderFemale,
      value: GENDER.FEMALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderDiverse,
      value: GENDER.DIVERSE,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionTwo.unknown,
      value: GENDER.UNSPECIFIED,
    },
  ];
};

const getEarlyRehabCriteriaOptions = (translations: Translations) => {
  return [
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .intensiveCareMonitoring,
      value: EARLY_REHAB_CRITERIA.INTENSIVE_CARE_MONITORING,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .intermittentVentilation,
      value: EARLY_REHAB_CRITERIA.INTERMITTENT_VENTILATION,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.behaviouralDisorder,
      value: EARLY_REHAB_CRITERIA.BEHAVIOURAL_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .communicationDisorder,
      value: EARLY_REHAB_CRITERIA.COMMUNICATION_DISORDER,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.tracheostoma,
      value: EARLY_REHAB_CRITERIA.TRACHEOSTOMA,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.orientationDisorder,
      value: EARLY_REHAB_CRITERIA.ORIENTATION_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.swallowingDisorder,
      value: EARLY_REHAB_CRITERIA.SWALLOWING_DISORDER,
    },
  ];
};

const getDoctorAuthorizationOptions = (translations: Translations) => {
  return [
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationOptionOne,
      value: 1,
    },
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationQuestionTwo,
      value: 2,
    },
  ];
};

export const GKVMedicalFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.GKV_MEDICAL
>["sections"] = [
  // Section One: Patient Information
  {
    fields: {
      admission_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.intakeDate}
            name="admission_date"
          />
        ),
        position: 5,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionOne
              .hospitalStayInformation,
        },
      },
      discharge_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionOne.dischargeDate
            }
            name="discharge_date"
          />
        ),
        position: 6,
      },
      early_rehabilitation_measures: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              aria-label={
                translations.patientForms.medicalForm.sectionOne
                  .earlyRehabMeasures
              }
              isRequired={!skipRequiredValidation}
              name="early_rehabilitation_measures"
              options={options}
              size="md"
            />
          );
        },
        position: 7,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionOne.earlyRehabMeasures,
        },
      },
      gender: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = getGenderOptions(translations);
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={translations.patientForms.drvGeneralForm.sectionTwo.gender}
              name="gender"
              options={options}
              size="md"
            />
          );
        },
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionOne
              .informationInsuredPerson,
        },
      },
      insurance_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson
                .insuranceNumber
            }
            name="insurance_number"
            placeholder=" "
          />
        ),
        position: 4,
      },
      patient_birthdate: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson.dateOfBirth
            }
            maxValue={getUnixTime(new Date())}
            name="patient_birthdate"
          />
        ),
        position: 3,
      },
      patient_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.firstName}
            name="patient_first_name"
            placeholder=" "
          />
        ),
        position: 1,
      },
      patient_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.lastName}
            name="patient_last_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      rehab_admission_type: {
        input: RehabAdmissionTypeField,
        position: 8,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionOne.admissionRehab,
        },
      },
    },
    position: 1,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionOne.title,
  },
  // Section Two: Function Diagnoses
  {
    fields: {
      complication_in_treatment_process: {
        input: ComplicationInTreatmentProcessField,
        position: 4,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionTwo.complications,
        },
      },
      icd_code_1_diagnosis: {
        hideSource: true,
        input: ICDCodeFields,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionTwo.functionDiagnoses,
        },
      },
      infections_state: {
        input: InfectionsStateField,
        position: 5,
      },
      other_treatments: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionTwo.otherTreatments
            }
            name="other_treatments"
          />
        ),
        position: 3,
      },
      surgery_performed: {
        input: SurgeryPerformedField,
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionTwo
              .performedTreatments,
        },
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionTwo.title,
  },
  // Singer Patient Profile
  {
    fields: {
      singer_assessment_concentration: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .taskConcentration
            }
            name="singer_assessment_concentration"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 17,
      },
      singer_assessment_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            className="w-[50%]"
            isRequired={!skipRequiredValidation}
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .creationDate
            }
            maxValue={getUnixTime(new Date())}
            name="singer_assessment_date"
          />
        ),
        position: 1,
        subTitle: {
          text: ({ translations }: { translations: Translations }) =>
            translations.patientForms.medicalForm.sectionTwo
              .longTermImpairments,
        },
      },
      singer_assessment_domestic_life_household: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .domesticLifeHousehold
            }
            name="singer_assessment_domestic_life_household"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 20,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.domesticLife}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainDomesticLife
              }
            />
          ),
        },
      },
      singer_assessment_dressing_and_undressing: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyDress
            }
            name="singer_assessment_dressing_and_undressing"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 3,
      },
      singer_assessment_food: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyEat
            }
            name="singer_assessment_food"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 2,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.selfsufficiency}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainSelfsufficiency
              }
            />
          ),
        },
      },
      singer_assessment_hygiene: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyHygiene
            }
            name="singer_assessment_hygiene"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 3,
      },
      singer_assessment_listening: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .communicationListening
            }
            name="singer_assessment_listening"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 12,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.communication}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainCommunication
              }
            />
          ),
        },
      },
      singer_assessment_orientation: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .taskOrientation
            }
            name="singer_assessment_orientation"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 16,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.task}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainTask
              }
            />
          ),
        },
      },
      singer_assessment_planning: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .taskPlanning
            }
            name="singer_assessment_planning"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 18,
      },
      singer_assessment_reading: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .learnRead
            }
            name="singer_assessment_reading"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 14,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.learn}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainLearn
              }
            />
          ),
        },
      },
      singer_assessment_shower: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyShower
            }
            name="singer_assessment_shower"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 4,
      },
      singer_assessment_social_interaction: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .socialInteraction
            }
            name="singer_assessment_social_interaction"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 19,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.socialInteraction}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainSocialInteraction
              }
            />
          ),
        },
      },
      singer_assessment_stairs: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .mobilityStairs
            }
            name="singer_assessment_stairs"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 11,
      },
      singer_assessment_stool: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyStool
            }
            name="singer_assessment_stool"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 4,
      },
      singer_assessment_talking: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .communicationTalk
            }
            name="singer_assessment_talking"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 13,
      },
      singer_assessment_toilet_use: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyToilet
            }
            name="singer_assessment_toilet_use"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 6,
      },
      singer_assessment_transfer: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .mobilityTransfer
            }
            name="singer_assessment_transfer"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 8,
        subTitle: {
          text: ({ translations }) => (
            <SingerAssessmentSectionSubTitle
              scoreFieldNames={SINGER_DOMAIN_SCORE_FIELDS.mobility}
              subtitle={
                translations.patient.medicalDiagnosis.singerPatientProfile
                  .singerDomainMobility
              }
            />
          ),
        },
      },
      singer_assessment_urination: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .selfsufficiencyUrine
            }
            name="singer_assessment_urination"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 5,
      },
      singer_assessment_walking: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .mobilityWalk
            }
            name="singer_assessment_walking"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 10,
      },
      singer_assessment_wheelchair: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .mobilityWheelchair
            }
            name="singer_assessment_wheelchair"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 9,
      },
      singer_assessment_writing: {
        input: ({ translations }) => (
          <ControlledRadioGroup
            label={
              translations.patient.medicalDiagnosis.singerPatientProfile
                .learnWrite
            }
            name="singer_assessment_writing"
            options={getSingerRadioOptions(translations)}
            orientation="horizontal"
            size="md"
          />
        ),
        position: 15,
      },
    },
    position: 3,
    title: () => <SingerAssessmentSectionTitle />,
  },
  // Section Three: Rehabilitation Capacity
  {
    fields: {
      interim_prosthesis_fitted: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionThree.prosthesis
              }
              name="interim_prosthesis_fitted"
              options={options}
              size="md"
            />
          );
        },
        position: 3,
      },
      limited_capacity: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              aria-label={
                translations.patientForms.medicalForm.sectionThree.resilience
              }
              name="limited_capacity"
              options={options}
              size="md"
            />
          );
        },
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionThree.resilience,
        },
      },
      reha_capacity: {
        input: RehaCapacityField,
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionThree
              .musculoskeletalSystem,
        },
      },
      reha_capacity_early_rehab_criteria: {
        input: ({ translations }) => {
          const options = getEarlyRehabCriteriaOptions(translations);
          return (
            <ControlledCheckboxGroupWithFocus
              aria-label={
                translations.patientForms.medicalForm.sectionThree
                  .earlyRehabIndex
              }
              name="reha_capacity_early_rehab_criteria"
              options={options}
              size="md"
            />
          );
        },
        position: 5,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionThree.earlyRehabIndex,
        },
      },
      reha_capacity_other: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={translations.patientForms.medicalForm.sectionThree.specifics}
            name="reha_capacity_other"
          />
        ),
        position: 4,
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionThree.title,
  },
  // Section Four: Rehabilitation Goals
  {
    fields: {
      rehabilitation_goals: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledTextarea
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionFour.rehabGoals}
            name="rehabilitation_goals"
          />
        ),
        position: 1,
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionFour.title,
  },
  // Section Five: Rehabilitation Focus
  {
    fields: {
      recommended_reha_type: {
        input: RecommendedRehaTypeField,
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionFive.typeOfRehab,
        },
      },
      rehab_directive_state: {
        input: RehabDirectiveStateField,
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionFive.rehabRl,
        },
      },
      rehab_focus: {
        input: RehabFocusField,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionFive.rehabFocus,
        },
      },
    },
    position: 6,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionFive.title,
  },
  // Section Six: Special Requirements and Transport
  {
    fields: {
      approved_by: {
        input: ({ skipRequiredValidation, translations }) => {
          if (skipRequiredValidation) return null;
          const options = getDoctorAuthorizationOptions(translations);
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.insuranceApp.eligibilityTab.approvalFlow
                  .authorisationQuestionTitle
              }
              name="approved_by"
              options={options}
            />
          );
        },
        position: 9,
      },
      doctor_exam_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionSix
                .doctorExamDateLabel
            }
            name="doctor_exam_date"
          />
        ),
        position: 10,
      },
      doctor_in_charge_in_hospital_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.firstName}
            name="doctor_in_charge_in_hospital_first_name"
            placeholder=" "
          />
        ),
        position: 6,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionSix
              .contactInformationDoctor,
        },
      },
      doctor_in_charge_in_hospital_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="doctor_in_charge_in_hospital_last_name"
            placeholder=" "
          />
        ),
        position: 7,
      },
      doctor_in_charge_in_hospital_phone: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionSix.phoneDoctor}
            name="doctor_in_charge_in_hospital_phone"
            placeholder=" "
            type="tel"
          />
        ),
        position: 8,
      },
      patient_body_height: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledNumberInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionSix.bodyHeight}
            name="patient_body_height"
            placeholder=" "
          />
        ),
        position: 2,
      },
      patient_body_weight: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledNumberInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionSix.bodyWeight}
            name="patient_body_weight"
            placeholder=" "
          />
        ),
        position: 3,
      },
      require_accompanying_person: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionSix
                  .accompanyingPerson
              }
              name="require_accompanying_person"
              options={options}
              size="md"
            />
          );
        },
        position: 5,
      },
      special_requirements_required: {
        input: SpecialRequirementsRequiredField,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionSix
              .specialRequirements,
        },
      },
      transport_type: {
        input: TransportTypeField,
        position: 4,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.medicalForm.sectionSix.transportation,
        },
      },
    },
    position: 7,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionSix.title,
  },
];
